import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type MarkerType = "BUY" | "SELL" | "DIVIDEND";
type RangeKey = "1M" | "3M" | "6M" | "1J" | "2J" | "3J" | "5J" | "YTD" | "MAX";

interface Quote {
  asOf: string;
  priceCents: number;
  currency: string;
}

interface Transaction {
  date: string;
  type: "BUY" | "SELL" | "DIVIDEND" | "TRANSFER";
  shares: string | null;
  priceCents: number | null;
  amountCents: number;
  feeCents: number;
  taxCents: number;
  currency: string;
}

interface Props {
  quotes: Quote[];
  transactions: Transaction[];
  currency: string;
  height?: number;
}

interface ChartPoint {
  /** Unix-ms — numerische X-Achse (statt Category) damit Marker auf jedem
   *  Tx-Tag positioniert werden koennen, auch wenn er nicht in chartData ist. */
  t: number;
  /** ISO yyyy-mm-dd fuer Tooltip-Anzeige. */
  date: string;
  priceCents: number;
}

interface Marker {
  t: number;
  date: string;
  priceCents: number;
  type: MarkerType;
  shares: string | null;
  txPriceCents: number | null;
  amountCents: number;
  feeCents: number;
  taxCents: number;
  currency: string;
}

const MARKER_COLOR: Record<MarkerType, string> = {
  BUY: "#16a34a",
  SELL: "#dc2626",
  DIVIDEND: "#3b82f6",
};

const RANGES: { key: RangeKey; days: number | null }[] = [
  { key: "1M", days: 30 },
  { key: "3M", days: 90 },
  { key: "6M", days: 180 },
  { key: "1J", days: 365 },
  { key: "2J", days: 2 * 365 },
  { key: "3J", days: 3 * 365 },
  { key: "5J", days: 5 * 365 },
  { key: "YTD", days: -1 },
  { key: "MAX", days: null },
];

function isoDate(s: string): string {
  return s.slice(0, 10);
}

function dateToTs(s: string): number {
  // "YYYY-MM-DD" -> ms (UTC midnight, identisch zu new Date(...).getTime() in modernen Engines)
  return new Date(`${s.slice(0, 10)}T00:00:00Z`).getTime();
}

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDateTick(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

function rangeStartTs(range: RangeKey, latestTs: number): number | null {
  if (range === "MAX") return null;
  if (range === "YTD") {
    const y = new Date(latestTs).getUTCFullYear();
    return Date.UTC(y, 0, 1);
  }
  const r = RANGES.find((x) => x.key === range);
  if (!r || r.days === null) return null;
  return latestTs - r.days * 24 * 3600 * 1000;
}

export function QuoteHistoryChart({ quotes, transactions, currency, height = 320 }: Props) {
  const { t } = useTranslation();
  const [range, setRange] = useState<RangeKey>("MAX");

  // Currency-Strategie:
  // - Quotes mit currency != Position-Currency wuerden Linien-Spruenge ohne
  //   FX-Konversion erzeugen.
  // - Wenn die Position-Currency mindestens eine Quote hat: filter auf diese
  //   Currency, blende den Rest aus (Hinweis dazu).
  // - Wenn die Position-Currency KEINE Quote hat: fallback auf die dominante
  //   Quote-Currency, damit der User wenigstens IRGENDWAS sieht.
  const currencyStats = useMemo(() => {
    const byCurrency = new Map<string, number>();
    for (const q of quotes) {
      byCurrency.set(q.currency, (byCurrency.get(q.currency) ?? 0) + 1);
    }
    return byCurrency;
  }, [quotes]);

  const chartCurrency = useMemo(() => {
    if ((currencyStats.get(currency) ?? 0) > 0) return currency;
    let best = currency;
    let max = -1;
    for (const [cur, count] of currencyStats) {
      if (count > max) {
        max = count;
        best = cur;
      }
    }
    return best;
  }, [currencyStats, currency]);

  const mismatchCount = useMemo(
    () => quotes.filter((q) => q.currency !== chartCurrency).length,
    [quotes, chartCurrency],
  );

  const fullChartData = useMemo<ChartPoint[]>(() => {
    if (quotes.length === 0) return [];
    return quotes
      .filter((q) => q.currency === chartCurrency)
      .map((q) => {
        const date = isoDate(q.asOf);
        return { t: dateToTs(date), date, priceCents: q.priceCents };
      })
      .sort((a, b) => a.t - b.t);
  }, [quotes, chartCurrency]);

  const rangeStartMs = useMemo(() => {
    if (fullChartData.length === 0) return null;
    const latest = fullChartData[fullChartData.length - 1]?.t ?? 0;
    return rangeStartTs(range, latest);
  }, [range, fullChartData]);

  const chartData = useMemo<ChartPoint[]>(() => {
    if (rangeStartMs === null) return fullChartData;
    return fullChartData.filter((p) => p.t >= rangeStartMs);
  }, [fullChartData, rangeStartMs]);

  // Marker: alle BUY/SELL/DIVIDEND-Transaktionen im sichtbaren Range, Y-Wert auf
  // den naechstgelegenen Quote-Preis gesnappt (damit Marker auf der Kurslinie
  // sitzen, statt im freien Raum). Numerische X-Achse erlaubt jeden Tx-Tag,
  // auch wenn keine Quote an dem Tag existiert.
  const markers = useMemo<Marker[]>(() => {
    if (chartData.length === 0) return [];
    const firstTs = chartData[0]?.t ?? 0;
    const lastTs = chartData[chartData.length - 1]?.t ?? 0;
    const result: Marker[] = [];
    for (const tx of transactions) {
      if (tx.type !== "BUY" && tx.type !== "SELL" && tx.type !== "DIVIDEND") continue;
      const txDate = isoDate(tx.date);
      const txTs = dateToTs(txDate);
      // Out-of-range nicht hart filtern: clamp auf Chart-Grenzen, damit alte
      // Tx wenigstens am Chart-Rand sichtbar bleiben (sonst stehen sie unsichtbar
      // links/rechts ausserhalb).
      const clampedTs = Math.min(Math.max(txTs, firstTs), lastTs);
      let nearestIdx = -1;
      let nearestDiff = Number.POSITIVE_INFINITY;
      for (let i = 0; i < chartData.length; i++) {
        const cp = chartData[i];
        if (!cp) continue;
        const diff = Math.abs(cp.t - clampedTs);
        if (diff < nearestDiff) {
          nearestDiff = diff;
          nearestIdx = i;
        }
      }
      if (nearestIdx < 0) continue;
      const snap = chartData[nearestIdx];
      if (!snap) continue;
      result.push({
        t: clampedTs,
        date: txDate,
        priceCents: snap.priceCents,
        type: tx.type as MarkerType,
        shares: tx.shares,
        txPriceCents: tx.priceCents,
        amountCents: tx.amountCents,
        feeCents: tx.feeCents,
        taxCents: tx.taxCents,
        currency: tx.currency,
      });
    }
    return result;
  }, [chartData, transactions]);

  const markersByTs = useMemo(() => {
    const m = new Map<number, Marker[]>();
    for (const mk of markers) {
      const arr = m.get(mk.t) ?? [];
      arr.push(mk);
      m.set(mk.t, arr);
    }
    return m;
  }, [markers]);

  // Defensiv Y-Domain berechnen — Werte in Cents, weil <Line dataKey="priceCents">
  // die YAxis-Skala in Cents fuehrt.
  const yDomain = useMemo<[number, number] | null>(() => {
    const allY: number[] = [
      ...chartData.map((q) => q.priceCents),
      ...markers.map((m) => m.priceCents),
    ].filter((n): n is number => Number.isFinite(n));
    if (allY.length === 0) return null;
    const rawMin = Math.min(...allY);
    const rawMax = Math.max(...allY);
    const range = rawMax - rawMin;
    const padding = Math.max(rawMax * 0.02, range * 0.05, 100);
    const yMin = Math.max(0, rawMin - padding);
    const yMax = rawMax + padding;
    return [yMin, yMax];
  }, [chartData, markers]);

  const xDomain = useMemo<[number, number] | null>(() => {
    if (chartData.length === 0) return null;
    const firstTs = chartData[0]?.t ?? 0;
    const lastTs = chartData[chartData.length - 1]?.t ?? 0;
    return [firstTs, lastTs];
  }, [chartData]);

  if (fullChartData.length === 0) {
    return <p className="text-sm text-slate-500">{t("investments.chart.no_history")}</p>;
  }

  if (yDomain === null || xDomain === null) {
    return (
      <div className="text-sm text-slate-500 p-4">
        {t("investments.detail.chart.empty", { defaultValue: "Keine Kurs-Daten verfuegbar." })}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-1">
        {RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setRange(r.key)}
            className={`px-2 py-0.5 text-xs rounded border ${
              range === r.key
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {t(`investments.chart.range.${r.key}`)}
          </button>
        ))}
      </div>
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis
              dataKey="t"
              type="number"
              domain={xDomain}
              scale="time"
              tickFormatter={formatDateTick}
              tick={{ fontSize: 11 }}
              minTickGap={32}
            />
            <YAxis
              tickFormatter={(v) => formatMoney(Number(v), chartCurrency)}
              tick={{ fontSize: 11 }}
              width={84}
              domain={yDomain}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null;
                const price = Number(payload[0]?.value ?? 0);
                const ts = Number(label);
                const labelStr = Number.isFinite(ts) ? formatDateTick(ts) : String(label);
                const mks = markersByTs.get(ts) ?? [];
                return (
                  <div className="bg-white border rounded shadow-sm px-3 py-2 text-xs">
                    <div className="font-medium mb-1">{labelStr}</div>
                    <div className="text-slate-700">
                      {t("investments.chart.tooltip.price")}: {formatMoney(price, chartCurrency)}
                    </div>
                    {mks.map((m, idx) => (
                      <div key={`${m.type}-${idx}`} className="mt-2 pt-2 border-t border-slate-200">
                        <div className="font-medium" style={{ color: MARKER_COLOR[m.type] }}>
                          {t(`investments.transaction.type_${m.type}`)}
                        </div>
                        {m.shares && (
                          <div>
                            {m.shares} {t("investments.chart.tooltip.shares")}
                            {m.txPriceCents !== null && m.txPriceCents > 0 && (
                              <> @ {formatMoney(m.txPriceCents, m.currency)}</>
                            )}
                          </div>
                        )}
                        <div>
                          {t("investments.chart.tooltip.amount")}:{" "}
                          {formatMoney(m.amountCents, m.currency)}
                        </div>
                        {m.feeCents > 0 && (
                          <div>
                            {t("investments.chart.tooltip.fee")}:{" "}
                            {formatMoney(m.feeCents, m.currency)}
                          </div>
                        )}
                        {m.taxCents > 0 && (
                          <div>
                            {t("investments.chart.tooltip.tax")}:{" "}
                            {formatMoney(m.taxCents, m.currency)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            <Line
              type="monotone"
              dataKey="priceCents"
              stroke="#2563eb"
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
            {markers.map((m, idx) => (
              <ReferenceDot
                key={`${m.type}-${m.t}-${idx}`}
                x={m.t}
                y={m.priceCents}
                r={6}
                fill={MARKER_COLOR[m.type]}
                stroke="#ffffff"
                strokeWidth={2}
                isFront
                ifOverflow="visible"
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <span className="flex items-center gap-1">
          <span style={{ color: MARKER_COLOR.BUY }}>●</span>
          {t("investments.transaction.type_BUY")}
        </span>
        <span className="flex items-center gap-1">
          <span style={{ color: MARKER_COLOR.SELL }}>●</span>
          {t("investments.transaction.type_SELL")}
        </span>
        <span className="flex items-center gap-1">
          <span style={{ color: MARKER_COLOR.DIVIDEND }}>●</span>
          {t("investments.transaction.type_DIVIDEND")}
        </span>
        {mismatchCount > 0 && (
          <span className="ml-auto text-amber-700">
            {chartCurrency === currency
              ? t("investments.chart.mismatch_hint", { count: mismatchCount, currency })
              : t("investments.chart.mismatch_fallback_hint", {
                  count: mismatchCount,
                  positionCurrency: currency,
                  chartCurrency,
                })}
          </span>
        )}
      </div>
    </div>
  );
}
